/**
 * Shared Apollo cache configuration (type policies).
 *
 * Extracted so the browser client (lib/apollo-client.ts) and a future RSC PreloadQuery client build
 * caches with IDENTICAL normalization — a hard requirement for `PreloadQuery` hydration: the keyFields
 * / merge policies must match on both sides or RSC-preloaded data won't line up with the client cache.
 *
 * Pure config only — no browser-only imports — so it's safe to import from a Server Component.
 */
import type { InMemoryCacheConfig } from '@apollo/client'

// Offset-based pagination merge for Hasura list fields. keyArgs excludes `limit`/`offset` so pages
// of different sizes share one cache entry; the merge places each page at its offset and trims stale
// trailing items left after deletions reduce the total count.
function offsetMerge(existing: unknown[] = [], incoming: unknown[], args: Record<string, unknown> | null) {
  const offset = (args?.offset as number) || 0
  if (offset === 0) return incoming
  const merged = existing.slice(0, offset + incoming.length)
  for (let i = 0; i < incoming.length; i++) {
    merged[offset + i] = incoming[i]
  }
  return merged
}

/**
 * Query root fields that use the offset-merge policy above, i.e. whose cache entry accumulates
 * pages into ONE array instead of holding a single page. Exported because a classic paginated
 * table (`crud/hooks/useCrudList`) must slice its own page back out of that merged array — the
 * merge is built for "load more" surfaces, and a table that showed the accumulation would append
 * page 2 under page 1.
 */
export const PAGINATED_QUERY_FIELDS: ReadonlySet<string> = new Set([
  'reservations',
  'guests',
  'housekeeping_tasks',
  'rooms',
  'call_history',
])

const paginated = {
  keyArgs: ['where', 'order_by'] as string[],
  merge: (existing: unknown[] = [], incoming: unknown[], { args }: { args: Record<string, unknown> | null }) =>
    offsetMerge(existing, incoming, args),
}

// Entities normalized by id. Missing an id in a selection set silently drops the object from cache.
const BY_ID = [
  'reservations', 'guests', 'rooms', 'room_types', 'folios', 'folio_transactions', 'invoices',
  'housekeeping_tasks', 'properties', 'tenants', 'reservation_status', 'room_status_types',
  'property_sources', 'property_payment_methods', 'guest_assignments', 'night_rates', 'users',
  'reservation_comments', 'maintenance_requests', 'room_closures', 'shift_schedules', 'staff_shifts',
  'drawer_sessions', 'invoices_line_items', 'tenant_members', 'property_settings', 'rack_rates',
  'channel_rates', 'taxes', 'pending_tasks', 'support_tickets', 'integration_settings',
  'email_domains', 'menu_items', 'roles', 'rules', 'guest_classes',
  // Group Blocks (NIZ-46): group_quotations normalizes the same way whether or
  // not `agency` is null (direct groups) — the quotation's own id is the key,
  // never a composite that involves the (possibly absent) agency. Shomoos
  // per-guest state (shomoos_transactions / shomoos_api_logs) is nested under
  // `reservations` in GroupBlockShomoosState and must normalize by its own id
  // too, or a poll re-merge silently drops the row instead of updating it.
  'group_quotations', 'travel_agencies', 'shomoos_transactions', 'shomoos_api_logs',
]

/** The shared InMemoryCache config. Each client wraps it with its own `new InMemoryCache(...)`. */
export function createCacheConfig(): InMemoryCacheConfig {
  return {
    typePolicies: {
      Query: {
        // Built from PAGINATED_QUERY_FIELDS so the exported set and the policy cannot drift.
        fields: Object.fromEntries([...PAGINATED_QUERY_FIELDS].map(f => [f, paginated])),
      },
      // Note: reservation_night_rates, room_status_histories, reservation_status_history are accessed
      // via aggregates / nested rels and may not include id — intentionally NOT normalized here.
      ...Object.fromEntries(BY_ID.map((t) => [t, { keyFields: ['id'] }])),
      // `group_block_actuals` is a VIEW and has no `id` column — its identity is
      // the block it derives from. Without this policy Apollo logs "Missing
      // field 'id'" and drops the object, so the Group Blocks list would lose
      // its Rooms and Paid columns on the second render of a cached row.
      group_block_actuals: { keyFields: ['block_id'] },
      // The Reservations list's group view (TKT-20261027-0004): a group row's id is its
      // group_quotation_id, and a group with rooms at two properties is one row PER property.
      reservation_list_entries: { keyFields: ['id', 'property_id'] },
      // The growth app catalogue (PR-8) is keyed by its `code` primary key; it has no `id`.
      growth_apps: { keyFields: ['code'] },
      growth_app_domains: { keyFields: ['code'] },
      // Season calendar cities (SW-9) are keyed by their `key` primary key; no `id`.
      growth_season_cities: { keyFields: ['key'] },
      // Wave H function rows (work_queue / nav_counts / home_kpis) are per-call results: their
      // ids (e.g. 'won_paid_q') repeat across homes and sessions, so they must never normalize.
      growth_work_queue_rows: { keyFields: false },
      growth_nav_count_rows: { keyFields: false },
      growth_home_kpi_rows: { keyFields: false },
    },
  }
}
